/**
 * TemplateInstallationsModal.tsx — „Klienci" szablonu (Krok 5).
 *
 * Pokazuje którzy klienci mają zainstalowany szablon i na jakiej wersji są przypięci, plus
 * znacznik „dostępna aktualizacja". Akcje serwisowe ITE (gating module_builder.support):
 *   - „Zastosuj u klienta" — wgrywa bezpieczne zmiany najnowszej wersji (POST .../apply-update);
 *     gdy aktualizacja jest ryzykowna (breaking) → przycisk zablokowany + znacznik „breaking",
 *   - „Cofnij do…" — miękkie cofnięcie klienta do starszej wydanej wersji (pola dodane po niej
 *     znikają, dane zostają).
 * Konsument GET /admin/module-templates/:slug/installations + .../versions;
 * POST .../rollback + .../apply-update.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { Modal } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useReservedFieldSlugToast } from '@/hooks/useReservedFieldSlugToast';
import { api } from '@/lib/api';

interface Installation {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  status: string;
  installedClientVersion: string | null;
  installedAt: string | null;
  updateAvailable: boolean;
  /** Czy dostępna aktualizacja zawiera zmiany ryzykowne (blokuje samoobsługowe „Zastosuj"). */
  updateHasBreaking: boolean;
}

interface VersionEntry {
  clientVersion: string;
}

/** Porównanie wersji klienta X.Y: >0 gdy a nowsza. */
function compareVer(a: string, b: string): number {
  const [aMaj = 0, aMin = 0] = a.split('.').map(Number);
  const [bMaj = 0, bMin = 0] = b.split('.').map(Number);
  return aMaj !== bMaj ? aMaj - bMaj : aMin - bMin;
}

function extractErrorCode(err: unknown): string | undefined {
  return (err as AxiosError<{ error?: string }>).response?.data?.error;
}

export function TemplateInstallationsModal({
  open,
  onClose,
  templateSlug,
}: {
  open: boolean;
  onClose: () => void;
  templateSlug: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const showReservedSlugToast = useReservedFieldSlugToast();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  // Akcje serwisowe (Zastosuj / Cofnij) dostępne tylko opiekunowi; projektant widzi listę
  // read-only (hub Krok 5). Super-admin ma oba uprawnienia.
  const canService = hasPermission('module_builder.support');
  // Wybrana wersja docelowa per klient (tenantId → 'X.Y').
  const [target, setTarget] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<{
    tenantId: string;
    name: string;
    version: string;
  } | null>(null);
  // Klient, któremu opiekun aplikuje najnowszą wersję (potwierdzenie przed POST).
  const [pendingApply, setPendingApply] = useState<{ tenantId: string; name: string } | null>(null);

  const query = useQuery<{ latestClientVersion: string; installations: Installation[] }>({
    queryKey: ['admin', 'module-templates', templateSlug, 'installations'],
    queryFn: async () =>
      (await api.get(`/admin/module-templates/${templateSlug}/installations`)).data,
    enabled: open,
    retry: false,
  });

  const versionsQuery = useQuery<{ versions: VersionEntry[] }>({
    queryKey: ['admin', 'module-templates', templateSlug, 'versions'],
    queryFn: async () => (await api.get(`/admin/module-templates/${templateSlug}/versions`)).data,
    enabled: open,
    retry: false,
  });

  const installations = useMemo(() => query.data?.installations ?? [], [query.data]);
  const versions = useMemo(() => versionsQuery.data?.versions ?? [], [versionsQuery.data]);

  const rollbackMutation = useMutation({
    mutationFn: async (vars: { tenantId: string; targetClientVersion: string }) =>
      (await api.post(`/admin/module-templates/${templateSlug}/rollback`, vars)).data,
    onSuccess: () => {
      toast.show({
        variant: 'success',
        description: t('moduleTemplateEditor.installations.rollbackSuccess'),
      });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'module-templates', templateSlug, 'installations'],
      });
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      const map: Record<string, string> = {
        TENANT_UPDATING: t('admin.tenants.dbUpdatingError'),
        NOT_OLDER: t('moduleTemplateEditor.installations.rollbackNotOlder'),
      };
      toast.show({
        variant: 'error',
        description: (code && map[code]) || t('moduleTemplateEditor.installations.rollbackError'),
      });
    },
  });

  const applyMutation = useMutation({
    mutationFn: async (vars: { tenantId: string }) =>
      (await api.post(`/admin/module-templates/${templateSlug}/apply-update`, vars)).data,
    onSuccess: () => {
      toast.show({
        variant: 'success',
        description: t('moduleTemplateEditor.installations.applySuccess'),
      });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'module-templates', templateSlug, 'installations'],
      });
    },
    onError: (err) => {
      // 400 FIELD_SLUG_RESERVED: nowe pole szablonu ma nazwę techniczną zarezerwowaną przez
      // system (np. „decisions") — aktualizacja cofnięta w całości; komunikat wskazuje pole.
      // Tytuł = ogólny błąd aktualizacji, opis = powód; zostaje do ręcznego zamknięcia (wspólny hak).
      if (
        showReservedSlugToast(err, {
          title: t('moduleTemplateEditor.installations.applyError'),
          variant: 'template',
        })
      ) {
        return;
      }
      const code = extractErrorCode(err);
      const map: Record<string, string> = {
        TENANT_UPDATING: t('admin.tenants.dbUpdatingError'),
        NOTHING_TO_APPLY: t('moduleTemplateEditor.installations.applyNothing'),
        BREAKING_REQUIRES_SUPPORT: t('moduleTemplateEditor.installations.applyBreaking'),
      };
      toast.show({
        variant: 'error',
        description: (code && map[code]) || t('moduleTemplateEditor.installations.applyError'),
      });
    },
  });

  /** Starsze wydane wersje niż ta zainstalowana u klienta (kandydaci do cofnięcia). */
  function olderVersions(installed: string | null): string[] {
    if (!installed) return [];
    return versions
      .map((v) => v.clientVersion)
      .filter((v) => compareVer(v, installed) < 0)
      .sort((a, b) => compareVer(b, a));
  }

  return (
    <>
      <Modal open={open} onClose={onClose} title={t('moduleTemplateEditor.installations.title')}>
        <div className="flex flex-col gap-3" data-testid="template-installations">
          {query.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : query.isError ? (
            <p className="text-sm text-red-600 dark:text-red-400">
              {t('moduleTemplateEditor.installations.loadError')}
            </p>
          ) : installations.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('moduleTemplateEditor.installations.empty')}
            </p>
          ) : (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('moduleTemplateEditor.installations.latest', {
                  version: query.data?.latestClientVersion ?? '—',
                })}
              </p>
              <ul className="flex flex-col gap-2">
                {installations.map((it) => {
                  const older = olderVersions(it.installedClientVersion);
                  return (
                    <li
                      key={it.tenantId}
                      className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
                      data-testid={`installation-${it.tenantId}`}
                    >
                      <span className="font-medium text-text-primary">{it.tenantName}</span>
                      <code className="text-xs text-gray-500 dark:text-gray-400">
                        {it.tenantSlug}
                      </code>
                      {it.installedClientVersion ? (
                        <Badge color="blue">v{it.installedClientVersion}</Badge>
                      ) : (
                        <Badge color="gray">
                          {t('moduleTemplateEditor.installations.unknownVersion')}
                        </Badge>
                      )}
                      {it.updateAvailable && !it.updateHasBreaking && (
                        <Badge color="amber">
                          {t('moduleTemplateEditor.installations.updateAvailable')}
                        </Badge>
                      )}
                      {it.updateAvailable && it.updateHasBreaking && (
                        <Badge color="red">
                          {t('moduleTemplateEditor.installations.updateBreaking')}
                        </Badge>
                      )}

                      <div className="ml-auto flex items-center gap-2">
                        {/* Zastosuj najnowszą — tylko opiekun, tylko gdy aktualizacja bezpieczna. */}
                        {canService && it.updateAvailable && !it.updateHasBreaking && (
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={applyMutation.isPending}
                            onClick={() =>
                              setPendingApply({ tenantId: it.tenantId, name: it.tenantName })
                            }
                            data-testid={`apply-btn-${it.tenantId}`}
                          >
                            {t('moduleTemplateEditor.installations.apply')}
                          </Button>
                        )}
                        {canService && older.length > 0 && (
                          <>
                            <Dropdown
                              aria-label={t('moduleTemplateEditor.installations.rollbackTo')}
                              options={[
                                {
                                  value: '',
                                  label: t('moduleTemplateEditor.installations.rollbackPick'),
                                },
                                ...older.map((v) => ({ value: v, label: `v${v}` })),
                              ]}
                              value={target[it.tenantId] ?? ''}
                              onChange={(v) => setTarget((m) => ({ ...m, [it.tenantId]: v }))}
                              className="w-auto"
                            />
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={!target[it.tenantId] || rollbackMutation.isPending}
                              onClick={() =>
                                setPending({
                                  tenantId: it.tenantId,
                                  name: it.tenantName,
                                  version: target[it.tenantId]!,
                                })
                              }
                              data-testid={`rollback-btn-${it.tenantId}`}
                            >
                              {t('moduleTemplateEditor.installations.rollback')}
                            </Button>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        open={pending !== null}
        variant="primary"
        title={t('moduleTemplateEditor.installations.rollbackConfirmTitle')}
        message={
          pending
            ? t('moduleTemplateEditor.installations.rollbackConfirmMessage', {
                name: pending.name,
                version: pending.version,
              })
            : ''
        }
        confirmLabel={t('moduleTemplateEditor.installations.rollbackConfirm')}
        onConfirm={() => {
          if (pending) {
            rollbackMutation.mutate({
              tenantId: pending.tenantId,
              targetClientVersion: pending.version,
            });
            setPending(null);
          }
        }}
        onClose={() => setPending(null)}
      />

      <ConfirmDialog
        open={pendingApply !== null}
        variant="primary"
        title={t('moduleTemplateEditor.installations.applyConfirmTitle')}
        message={
          pendingApply
            ? t('moduleTemplateEditor.installations.applyConfirmMessage', {
                name: pendingApply.name,
                version: query.data?.latestClientVersion ?? '',
              })
            : ''
        }
        confirmLabel={t('moduleTemplateEditor.installations.applyConfirm')}
        onConfirm={() => {
          if (pendingApply) {
            applyMutation.mutate({ tenantId: pendingApply.tenantId });
            setPendingApply(null);
          }
        }}
        onClose={() => setPendingApply(null)}
      />
    </>
  );
}
